import { Apis } from "../../lib/Apis";

const getApiError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const unwrapCategoriesPayload = (body) => {
  const payload = body?.data ?? body ?? {};
  const categories =
    payload?.categories ||
    (Array.isArray(payload?.data) ? payload.data : null) ||
    (Array.isArray(payload) ? payload : []) ||
    [];
  const pagination = payload?.pagination ?? null;
  const totalCount =
    pagination?.totalCount ??
    pagination?.totalRecords ??
    (Array.isArray(categories) ? categories.length : 0);
  return { payload, categories, pagination, totalCount };
};

/** Unwrap successResponse envelope used by blog APIs. */
const unwrapResponseBody = (body) => {
  if (body == null) return null;
  if (body?.success === false) {
    throw new Error(body?.message || "Request failed.");
  }
  // successResponse: { success, message, statusCode, data }
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    ("success" in body || "statusCode" in body)
  ) {
    return body.data;
  }
  return body;
};

const unwrapBlogsPayload = (body) => {
  const payload = unwrapResponseBody(body) ?? {};
  const blogs = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  const pagination = payload?.pagination ?? body?.pagination ?? null;
  const totalCount =
    pagination?.totalCount ??
    pagination?.totalRecords ??
    blogs.length;
  return { blogs, pagination, totalCount, payload };
};

// --- Hook from Blog/useHook.js ---
export const useBlog = () => {
  const getBlogCategories = async () => {
    try {
      const response = await Apis.getAllBlogCategories();
      const { categories } = unwrapCategoriesPayload(response?.data);
      return categories;
    } catch (error) {
      console.error("Error fetching blog categories:", error);
      throw new Error(getApiError(error, "Failed to fetch blog categories."));
    }
  };

  const postBlog = async (data) => {
    try {
      const response = await Apis.postBlog(data);
      return unwrapResponseBody(response?.data);
    } catch (error) {
      console.error("Error posting blog:", error);
      throw new Error(getApiError(error, "Failed to create blog."));
    }
  };

  const updateBlog = async (id, data) => {
    try {
      const response = await Apis.updateBlog(id, data);
      return unwrapResponseBody(response?.data);
    } catch (error) {
      console.error("Error updating blog:", error);
      throw error;
    }
  };

  const getBlogs = async (paginationParams = {}, searchParams = {}) => {
    try {
      const params = { ...paginationParams, ...searchParams };
      const response = await Apis.getBlogs(params);
      const { blogs, pagination } = unwrapBlogsPayload(response?.data);
      return {
        data: blogs,
        pagination,
      };
    } catch (error) {
      console.error("Error fetching blogs:", error);
      throw error;
    }
  };

  const searchBlogs = async (paginationParams = {}, searchParams = {}) => {
    try {
      const params = { ...paginationParams, ...searchParams };
      const response = await Apis.getBlogs(params);
      const { blogs, pagination } = unwrapBlogsPayload(response?.data);
      return {
        data: blogs,
        pagination,
      };
    } catch (error) {
      console.error(
        "🚀 ~ file: useHook.js ~ line 64 ~ searchBlogs ~ error",
        error,
      );
      throw error;
    }
  };

  const getBlogById = async (id) => {
    try {
      const response = await Apis.getBlogById(id);
      return unwrapResponseBody(response?.data);
    } catch (error) {
      console.error("Error fetching blog by id:", error);
      throw error;
    }
  };

  const addPopularBlog = async (data) => {
    try {
      const response = await Apis.addPopularBlog(data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error adding popular blog:", error);
      throw error;
    }
  };

  const addOnHeaderBlog = async (data) => {
    try {
      const response = await Apis.addOnHeaderBlog(data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error adding on header blog:", error);
      throw error;
    }
  };

  const deleteBlog = async (id) => {
    try {
      const response = await Apis.deleteBlog(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error deleting blog:", error);
      throw error;
    }
  };

  const showBlogOnHomeScreen = async (blogId, homeScreen) => {
    try {
      const data = {
        blogId,
        homeScreen: homeScreen ? 1 : 0,
      };
      const response = await Apis.addBlogToHomeScreen(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update home screen status.");
      }
      return unwrapResponseBody(body) ?? body;
    } catch (error) {
      console.error("Error updating home screen status:", error);
      throw error;
    }
  };

  const updateBlogPosition = async (blogId, newPosition) => {
    try {
      const response = await Apis.updateBlogPosition({
        blogId: Number(blogId),
        newPosition,
      });
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating blog position:", error);
      throw error;
    }
  };

  return {
    getBlogCategories,
    postBlog,
    updateBlog,
    getBlogs,
    getBlogById,
    addPopularBlog,
    addOnHeaderBlog,
    deleteBlog,
    showBlogOnHomeScreen,
    searchBlogs,
    updateBlogPosition,
  };
};

// --- Hook from BlogCategories/useHook.js ---
export const useBlogCategory = () => {
  const addBlogCategories = async (data) => {
    try {
      const response = await Apis.addCategoryBlog(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to add blog category.");
      }
      return body;
    } catch (error) {
      console.error("Error adding blog category:", error);
      throw new Error(getApiError(error, "Failed to add blog category."));
    }
  };

  const getBlogCategories = async (
    setBlogCategories,
    setTotal,
    offset = 0,
    limit = 10,
    name = "",
    status = "",
  ) => {
    try {
      const params = {};

      if (name && name.trim()) {
        params.name = name.trim();
      }

      if (status !== "") {
        params.status = status === "true" ? true : false;
      }

      const response = await Apis.getBlogCategories(offset, limit, params);
      const { categories, pagination, totalCount, payload } =
        unwrapCategoriesPayload(response?.data);

      setBlogCategories(categories);
      setTotal(totalCount);
      return { data: categories, pagination, totalCount, ...payload };
    } catch (error) {
      console.error("Error fetching blog categories:", error);
      throw new Error(getApiError(error, "Failed to fetch blog categories."));
    }
  };

  const searchBlogCategories = async (
    name,
    status,
    offset = 0,
    limit = 10,
  ) => {
    try {
      const params = {};

      if (name && name.trim()) {
        params.name = name.trim();
      }

      if (status !== "") {
        params.status = status === "true" ? true : false;
      }

      const response = await Apis.getBlogCategories(offset, limit, params);
      const { categories, pagination, totalCount, payload } =
        unwrapCategoriesPayload(response?.data);
      return { data: categories, pagination, totalCount, ...payload };
    } catch (error) {
      console.error(
        "🚀 ~ file: useHook.js ~ line 64 ~ searchBlogCategories ~ error",
        error,
      );
      throw new Error(getApiError(error, "Failed to search blog categories."));
    }
  };

  const updateBlogCategory = async (id, data) => {
    try {
      const response = await Apis.updateBlogCategory(id, data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update blog category.");
      }
      return body;
    } catch (error) {
      console.error("Error updating blog category:", error);
      throw new Error(getApiError(error, "Failed to update blog category."));
    }
  };

  const deleteBlogCategory = async (id) => {
    try {
      const response = await Apis.deleteBlogCategory(id);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to delete blog category.");
      }
      return body;
    } catch (error) {
      console.error("Error deleting blog category:", error);
      throw new Error(getApiError(error, "Failed to delete blog category."));
    }
  };

  const updateCategoryStatus = async (id, status) => {
    try {
      const response = await Apis.updateBlogCategoryStatus(id, status);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(
          body?.message || "Failed to update blog category status.",
        );
      }
      return body;
    } catch (error) {
      console.error("Error updating category status:", error);
      throw new Error(
        getApiError(error, "Failed to update blog category status."),
      );
    }
  };

  const getBlogByCategoryId = async (id, setBlogs) => {
    try {
      const response = await Apis.getBlogCategoryById(id);
      const payload = response?.data?.data ?? response?.data ?? {};
      const category = payload?.category ?? payload;
      const blogs = category?.blogs || [];
      if (typeof setBlogs === "function") setBlogs(blogs);
      return blogs;
    } catch (error) {
      console.error("Error fetching blog category by id:", error);
      throw new Error(getApiError(error, "Failed to fetch blog category."));
    }
  };

  const getBlogCategoryById = async (id, setCategory) => {
    try {
      const response = await Apis.getBlogCategoryById(id);
      const payload = response?.data?.data ?? response?.data ?? {};
      const category = payload?.category ?? payload;
      if (typeof setCategory === "function") setCategory(category);
      return category;
    } catch (error) {
      console.error("Error fetching blog category by id:", error);
      throw new Error(getApiError(error, "Failed to fetch blog category."));
    }
  };

  const updateBlogCategoryPosition = async (categoryId, newPosition) => {
    try {
      const response = await Apis.updateBlogCategoryPosition({
        categoryId,
        newPosition,
      });
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(
          body?.message || "Failed to update blog category position.",
        );
      }
      return body;
    } catch (error) {
      console.error("Error updating blog category position:", error);
      throw new Error(
        getApiError(error, "Failed to update blog category position."),
      );
    }
  };

  return {
    addBlogCategories,
    getBlogCategories,
    updateBlogCategory,
    deleteBlogCategory,
    updateCategoryStatus,
    getBlogByCategoryId,
    getBlogCategoryById,
    searchBlogCategories,
    updateBlogCategoryPosition,
  };
};
